import { Ionicons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { SearchInput } from '@/components/ui/search-input';
import { useCities, useStates } from '@/hooks/use-locations';
import { Region } from '@/types/discovery';
import { cn } from '@/utils/cn';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { KEYBOARD_BOTTOM_OFFSET } from '@/components/dashboard-scroll-view';

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

type CitySelectorSheetProps = {
  open: boolean;
  region: Region;
  onClose: () => void;
  onSelect: (region: Region) => void;
  /** Desligado quando quem escolhe é o prestador: "Brasil" não é um lugar de atendimento. */
  allowNationwide?: boolean;
  title?: string;
};

/**
 * Escolha da região em dois passos (estado → cidade), com busca.
 * "Todo o Brasil" é sempre uma saída válida: ninguém fica preso numa cidade vazia.
 */
export function CitySelectorSheet({
  open,
  region,
  onClose,
  onSelect,
  allowNationwide = true,
}: CitySelectorSheetProps) {
  const [uf, setUf] = useState<string | null>(region.uf);
  const [term, setTerm] = useState('');

  const { data: states, isLoading: loadingStates } = useStates();
  const { data: cities, isLoading: loadingCities } = useCities(uf);

  const filteredStates = useMemo(() => {
    if (!states) return [];
    if (!term.trim()) return states;
    const needle = normalize(term);
    return states.filter(
      (state) => normalize(state.name).includes(needle) || normalize(state.uf).includes(needle),
    );
  }, [states, term]);

  const filteredCities = useMemo(() => {
    if (!cities) return [];
    if (!term.trim()) return cities;
    const needle = normalize(term);
    return cities.filter((city) => normalize(city).includes(needle));
  }, [cities, term]);

  const handleClose = () => {
    setTerm('');
    setUf(region.uf);
    onClose();
  };

  const handleSelectCity = (city: string) => {
    if (!uf) return;
    setTerm('');
    onSelect({ uf, city });
    onClose();
  };

  const handleSelectBrazil = () => {
    setTerm('');
    setUf(null);
    onSelect({ uf: null, city: null });
    onClose();
  };

  return (
    <Modal visible={open} transparent animationType="slide" onRequestClose={handleClose}>
      <Pressable className="flex-1 justify-end bg-black/40" onPress={handleClose}>
        <Pressable
          onPress={(event) => event.stopPropagation()}
          className="h-[80%] rounded-t-3xl bg-card"
          style={{ borderCurve: 'continuous' }}
        >
          <View className="items-center pt-3">
            <View className="h-1 w-10 rounded-full bg-neutral-200" />
          </View>

          <View className="gap-3 p-5 pb-3">
            <View className="flex-row items-center justify-between">
              <ThemedText className="font-bold text-lg text-foreground">
                {uf ? 'Escolha a cidade' : 'Escolha o estado'}
              </ThemedText>

              <Pressable onPress={handleClose} hitSlop={10}>
                <Ionicons className="text-neutral-400" name="close" size={22} />
              </Pressable>
            </View>

            {uf ? (
              <Pressable
                onPress={() => {
                  setUf(null);
                  setTerm('');
                }}
                className="flex-row items-center gap-1 self-start"
                hitSlop={8}
              >
                <Ionicons name="chevron-back" size={14} color="#FD5001" />
                <ThemedText className="font-semibold text-sm text-primary">
                  Trocar estado ({uf})
                </ThemedText>
              </Pressable>
            ) : null}

            <SearchInput
              value={term}
              onChangeText={setTerm}
              placeholder={uf ? 'Buscar cidade' : 'Buscar estado'}
            />
          </View>

          <KeyboardAwareScrollView
            bottomOffset={KEYBOARD_BOTTOM_OFFSET}
            className="flex-1 px-5"
            keyboardShouldPersistTaps="handled"
          >
            {allowNationwide ? (
              <Pressable
                onPress={handleSelectBrazil}
                className={cn(
                  'mb-2 flex-row items-center gap-3 rounded-2xl border border-neutral-900/5 p-3',
                  !region.uf ? 'bg-primary-50' : 'bg-card',
                )}
                style={{ borderCurve: 'continuous' }}
              >
                <Ionicons name="globe-outline" size={18} color="#FD5001" />
                <ThemedText className="flex-1 font-semibold text-foreground">
                  Todo o Brasil
                </ThemedText>
                {!region.uf ? <Ionicons name="checkmark" size={18} color="#FD5001" /> : null}
              </Pressable>
            ) : null}

            {(uf ? loadingCities : loadingStates) ? (
              <View className="items-center py-8">
                <ActivityIndicator color="#FD5001" />
              </View>
            ) : null}

            {!uf
              ? filteredStates.map((state) => (
                  <Pressable
                    key={state.uf}
                    onPress={() => {
                      setUf(state.uf);
                      setTerm('');
                    }}
                    className="flex-row items-center justify-between border-b border-neutral-100 py-3"
                  >
                    <ThemedText className="text-foreground">{state.name}</ThemedText>
                    <View className="flex-row items-center gap-2">
                      <ThemedText className="text-xs text-neutral-400">{state.uf}</ThemedText>
                      <Ionicons className="text-neutral-300" name="chevron-forward" size={16} />
                    </View>
                  </Pressable>
                ))
              : filteredCities.map((city) => {
                  const isSelected = region.uf === uf && region.city === city;

                  return (
                    <Pressable
                      key={city}
                      onPress={() => handleSelectCity(city)}
                      className="flex-row items-center justify-between border-b border-neutral-100 py-3"
                    >
                      <ThemedText
                        className={cn(
                          'text-foreground',
                          isSelected && 'font-semibold text-primary',
                        )}
                      >
                        {city}
                      </ThemedText>
                      {isSelected ? <Ionicons name="checkmark" size={18} color="#FD5001" /> : null}
                    </Pressable>
                  );
                })}

            <View className="h-10" />
          </KeyboardAwareScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
